import type { UseMutationResult, UseQueryResult, DefaultError } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { ApiResponse } from '@course-planner/shared';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import {
  getCoursePlannerConfigPath,
  getCoursePlannerSchoolClusterGuidePath,
  getSucceedApiSchoolClusterGuidePath,
  getSucceedApiConfigUploadUrlPath,
  isDirectApiMode,
} from '../utils/api-paths';

function isMockMode(): boolean {
  return Boolean(import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true');
}

/** Grade-level credit settings (matches API gradeSettings item). */
export interface GradeSetting {
  grade: number;
  minCredits?: number;
  maxCredits?: number;
}

/** Grade-level minimum credits (matches API gradeDeficiency item). */
export interface GradeDeficiency {
  grade: number;
  minCredits?: number;
}

/** Labels object inside the config (maps term names e.g. cluster/pathway). */
export interface ConfigLabels {
  cluster?: string;
  pathway?: string;
}

export interface SchoolClusterGuideLink {
  name: string;
  url: string;
}

/** schoolClusterGuide — Additional resources shown on the student's Manage Plan page. */
export interface SchoolClusterGuide {
  /** Student-facing description / document name. */
  description?: string;
  /** Filename of the uploaded document (set by server after upload). */
  name?: string;
  /** Backend may return boolean; some mocks may return string. */
  isAvailable?: boolean | string;
  links?: SchoolClusterGuideLink[];
}

/** schoolClusterGuide sent in PUT payload — always use a boolean for isAvailable. */
export interface SchoolClusterGuidePayload {
  description?: string;
  name?: string;
  isAvailable?: boolean;
  links?: SchoolClusterGuideLink[];
}

/**
 * Response shape from GET course-planner/config.
 */
export interface CoursePlannerConfigResponse {
  labels?: ConfigLabels;
  termSetting?: boolean;
  terms?: string[];
  schoolSelection?: string;
  /** When schoolSelection is SPECIFIC_GRADES, which grades (e.g. 6–12) may select schools */
  schoolSelectionGrades?: number[];
  schoolClusterGuide?: SchoolClusterGuide;
  approvalRequirement?: string;
  planApprovalOption?: string;
  staffApprovalOption?: string;
  planFeatureOption?: string;
  gradeSettings?: GradeSetting[];
  gradeDeficiency?: GradeDeficiency[];
  [key: string]: unknown;
}

/** Payload for PUT course-planner/config. Same path as GET.
 * Note: schoolClusterGuide is intentionally excluded — the real backend
 * saves it via a separate PUT /config/school-cluster-guide endpoint.
 */
export interface PutCoursePlannerConfigPayload {
  labels?: ConfigLabels;
  termSetting?: boolean;
  terms?: string[];
  schoolSelection?: string;
  schoolSelectionGrades?: number[];
  approvalRequirement?: string;
  planApprovalOption?: string;
  staffApprovalOption?: string;
  planFeatureOption?: string;
  gradeSettings?: GradeSetting[];
  gradeDeficiency?: GradeDeficiency[];
}

/**
 * Merge a successful PUT /config payload into cached GET config.
 * schoolClusterGuide is saved via a separate endpoint and is not part of this payload.
 */
export function mergeCoursePlannerConfigCache(
  prev: CoursePlannerConfigResponse | undefined,
  payload: PutCoursePlannerConfigPayload
): CoursePlannerConfigResponse {
  const base: CoursePlannerConfigResponse = { ...(prev ?? {}) };
  return {
    ...base,
    ...payload,
    labels: payload.labels ? { ...base.labels, ...payload.labels } : base.labels,
  };
}

export interface UseGetCoursePlannerConfigOptions {
  /** When false, the query does not run (defaults to true). */
  enabled?: boolean;
}

/**
 * Fetch course-planner config (GET course-planner/config).
 * Same pattern as useGetSubjectAreas - uses same API client.
 */
export function useGetCoursePlannerConfig(
  options?: UseGetCoursePlannerConfigOptions
): UseQueryResult<CoursePlannerConfigResponse, DefaultError> {
  const apiClient = useApiClient();
  const path = getCoursePlannerConfigPath();

  return useQuery({
    queryKey: queryKeys.configurations.config(),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      if (isMockMode()) {
        const mockData = await import('../mock-data/course-planner-config.json');
        return (mockData.default ?? mockData) as CoursePlannerConfigResponse;
      }
      const response = await apiClient.get<
        ApiResponse<CoursePlannerConfigResponse> | CoursePlannerConfigResponse
      >(path);
      const body = response.data as Record<string, unknown>;
      // Real service returns flat config; mock service wraps in { success, data }
      return ((body?.data ?? body) as CoursePlannerConfigResponse) ?? {};
    },
    // Prevent automatic background refetches from overwriting unsaved form edits.
    // Explicit invalidation (after save) still works regardless of staleTime.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    throwOnError: false,
  });
}

/**
 * Update course-planner config (PUT same path as GET).
 * On success, merges the submitted payload into the config query cache (no invalidate/refetch)
 * so Additional resources and other fields are not wiped by a stale GET response.
 */
export function usePutCoursePlannerConfig(): UseMutationResult<
  CoursePlannerConfigResponse,
  DefaultError,
  PutCoursePlannerConfigPayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const path = getCoursePlannerConfigPath();

  return useMutation({
    mutationFn: async (payload: PutCoursePlannerConfigPayload) => {
      const response = await apiClient.put<CoursePlannerConfigResponse>(path, { config: payload });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Do not invalidate: a refetch can return `schoolClusterGuide` unchanged/missing from the
      // server and wipe what the user just saved in the client cache.
      queryClient.setQueryData<CoursePlannerConfigResponse>(
        queryKeys.configurations.config(),
        (prev) => mergeCoursePlannerConfigCache(prev, variables)
      );
    },
  });
}

/**
 * Update schoolClusterGuide (Additional resources) via the dedicated
 * PUT /config/school-cluster-guide endpoint.
 * The real backend ignores schoolClusterGuide in PUT /config — it must be
 * sent here separately to be persisted.
 */
export function usePutSchoolClusterGuide(): UseMutationResult<
  CoursePlannerConfigResponse,
  DefaultError,
  SchoolClusterGuidePayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const path = getCoursePlannerSchoolClusterGuidePath();

  return useMutation({
    mutationFn: async (payload: SchoolClusterGuidePayload) => {
      const response = await apiClient.put<CoursePlannerConfigResponse>(path, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Merge the saved schoolClusterGuide into the cached config so the UI
      // reflects what was persisted without a full refetch.
      queryClient.setQueryData<CoursePlannerConfigResponse>(
        queryKeys.configurations.config(),
        (prev) => ({
          ...(prev ?? {}),
          schoolClusterGuide: { ...prev?.schoolClusterGuide, ...variables },
        })
      );
    },
  });
}

/** Response from the document upload endpoint. */
export interface UploadSchoolClusterGuideDocumentResponse {
  /** Stored filename returned by the server. */
  name: string;
}

/**
 * Payload passed into useUploadSchoolClusterGuideDocument.mutateAsync().
 * Matches the fields sent to PUT /config/school-cluster-guide.
 */
export interface UploadSchoolClusterGuideDocumentPayload {
  file: File;
  isAvailable: boolean;
  description: string;
  name: string;
  links: SchoolClusterGuideLink[];
}

/**
 * Document upload hook for Additional resources (school cluster guide).
 *
 * Embedded (QA/prod) mode — matches legacy cp-ui staff upload exactly:
 *   1. GET /config/upload-url  → succeed-api returns a presigned S3 URL.
 *   2. In parallel (Promise.all):
 *      a. PUT the file bytes directly to the presigned S3 URL
 *         (Content-Type: application/octet-stream). This is what actually persists
 *         the file in S3 — succeed-api's POST only saves metadata.
 *      b. POST /config/school-cluster-guide FormData (file + metadata) to succeed-api,
 *         which proxies the metadata to course-planner-service as PUT.
 *
 * The direct S3 PUT was previously removed (assuming succeed-api uploaded the bytes
 * server-side), but that left the metadata saved with no file in S3 — so the student
 * download endpoint 500s. Restored to match legacy behavior.
 *
 * In direct/local mode (no succeed-api), only a metadata PUT is sent.
 */
export function useUploadSchoolClusterGuideDocument(): UseMutationResult<
  UploadSchoolClusterGuideDocumentResponse,
  DefaultError,
  UploadSchoolClusterGuideDocumentPayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const uploadMetaPath = getCoursePlannerSchoolClusterGuidePath();
  // In embedded mode, the POST goes directly to succeed-api at /config/school-cluster-guide
  // (no /course-planner/ prefix). This matches old cp-ui: UPLOAD_FILE_URL = '/config/school-cluster-guide'.
  const succeedApiPostPath = getSucceedApiSchoolClusterGuidePath();
  // GET presigned S3 upload URL from succeed-api (no /course-planner/ prefix).
  // Matches old cp-ui: CONFIG_UPLOAD_URL = '/config/upload-url'.
  const succeedApiUploadUrlPath = getSucceedApiConfigUploadUrlPath();

  return useMutation({
    mutationFn: async (
      payload: UploadSchoolClusterGuideDocumentPayload
    ): Promise<UploadSchoolClusterGuideDocumentResponse> => {
      const { file, isAvailable, description, name, links } = payload;

      if (isDirectApiMode()) {
        // Local mode: real service only supports PUT with JSON metadata (no file binary).
        const response = await apiClient.put<CoursePlannerConfigResponse>(uploadMetaPath, {
          isAvailable,
          description,
          name: name || file.name,
          links,
        });
        const responseData = response.data as CoursePlannerConfigResponse;
        const storedName =
          (responseData?.schoolClusterGuide as SchoolClusterGuide)?.name ?? name ?? file.name;
        return { name: storedName };
      }

      // Embedded (QA/prod) mode: first fetch the presigned S3 upload URL so we can PUT
      // the file bytes directly to S3 (legacy behavior — succeed-api only stores metadata).
      let presignedUploadUrl = '';
      if (file) {
        const uploadUrlResponse = await apiClient.get<{ url?: string }>(succeedApiUploadUrlPath);
        presignedUploadUrl = uploadUrlResponse.data?.url ?? '';
      }

      const fd = new FormData();
      if (file) {
        fd.append('file', file);
      }
      fd.append('isAvailable', isAvailable.toString());
      fd.append('description', description);
      fd.append('name', name || file.name);
      // links must be stringified — FormData only supports strings and Blob.
      fd.append('links', JSON.stringify(links));

      // POST to succeed-api at /config/school-cluster-guide (no /course-planner/ prefix).
      // Matches old cp-ui: UPLOAD_FILE_URL = '/config/school-cluster-guide'.
      //
      // IMPORTANT: Our axios instance defaults to Content-Type: application/json.
      // When axios sees FormData with that content-type, its transformRequest converts
      // the FormData to JSON (formDataToJSON), losing the file binary and breaking
      // multipart/form-data. We must prevent that by setting Content-Type: false in
      // the request config — axios 1.x AxiosHeaders treats `false` as a locked value
      // that stops the instance default from applying, and the XHR/fetch adapter then
      // lets the browser set the correct multipart/form-data; boundary=... header.
      //
      // In parallel, PUT the file bytes directly to the presigned S3 URL. This is the
      // step that actually persists the file in S3 (legacy cp-ui did the same). Use a
      // bare axios.put (NOT apiClient) so no auth header / baseURL is added to the S3 URL.
      const uploadTasks: Promise<unknown>[] = [
        apiClient.post<CoursePlannerConfigResponse>(succeedApiPostPath, fd, {
          headers: { 'Content-Type': false as unknown as string },
        }),
      ];
      if (file && presignedUploadUrl) {
        uploadTasks.push(
          axios.put(presignedUploadUrl, file, {
            headers: { 'Content-Type': 'application/octet-stream' },
          })
        );
      }
      const [metaResponse] = await Promise.all(uploadTasks);

      const responseData = (metaResponse as { data?: CoursePlannerConfigResponse })
        ?.data as CoursePlannerConfigResponse;
      const storedName =
        (responseData?.schoolClusterGuide as SchoolClusterGuide)?.name ?? name ?? file.name;
      return { name: storedName };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<CoursePlannerConfigResponse>(
        queryKeys.configurations.config(),
        (prev) => ({
          ...(prev ?? {}),
          schoolClusterGuide: {
            ...prev?.schoolClusterGuide,
            name: data.name,
            description: variables.description,
            isAvailable: variables.isAvailable,
            links: variables.links,
          },
        })
      );
    },
  });
}
